import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { StripeElementStyle, loadStripe } from "@stripe/stripe-js";
import Button from "_shared/components/Button/Button";
import InputField from "_shared/components/InputField/InputField";
import CustomModal from "_shared/components/Modal/Modal";
import SelectField from "_shared/components/SelectField/SelectField";
import countries from "_shared/constants/countries";
import { useModal } from "_shared/contexts/ModalContext";
import { API } from "aws-amplify";
import clsx from "clsx";
import { stripeKey } from "env";
import { Formik } from "formik";
import styles from "./AddCardModal.module.scss";

const stripePromise = loadStripe(stripeKey);

const elementStyle: StripeElementStyle = {
  base: {
    color: "#ffffff",
    "::placeholder": {
      color: "#868686",
    },
  },
  invalid: {
    color: "#ffffff",
  },
};
const elementOptions = {
  style: elementStyle,
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddCardModal = ({ open, onClose, onSuccess }: Props) => {
  const elements = useElements();
  const stripe = useStripe();
  const { onErrorMsg } = useModal();

  const handleSubmit = async ({
    name,
    country,
    zipCode,
  }: {
    name: string;
    country: string;
    zipCode: string;
  }) => {
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    //Front-end validation for all required fields for payment submission.
    let message = "";
    if (name.length === 0) {
      message = `You must enter your full name`;
    } else if (country.length === 0) {
      message = `You must select a country or region`;
    } else if (zipCode.length === 0) {
      message = `You must enter your zip code`;
    }

    if (message) {
      onErrorMsg(message);
      return;
    }
    const addCard = await API.post("auth", "/cognito/addCard", {});
    const result = await stripe.confirmCardSetup(addCard.client_secret, {
      payment_method: {
        card: elements.getElement(CardNumberElement)!,
        billing_details: {
          name: addCard.name,
          email: addCard.email,
        },
      },
    });

    // If only one card exists, set it as the default card
    const getListCards = await API.post("auth", "/cognito/listCards", {});
    if (getListCards.length === 1) {
      await API.post("auth", "/cognito/setPrimary", {
        body: { payment_method_id: getListCards[0].id },
      });
    }

    if (result.error) {
      onErrorMsg(
        result.error.message ?? "There was a problem adding the card to Stripe",
      );
    } else {
      onSuccess();
    }
  };

  return (
    <CustomModal open={open} onClose={onClose} title="Add a card">
      <div data-testid="form-add-card">
        <Formik
          initialValues={{
            name: "",
            country: countries[0],
            zipCode: "",
          }}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className={clsx(styles.form, "input-container")}>
                <InputField dark name="name" label="Name on card" />
                <SelectField dark name="country" options={countries} />
                <InputField dark name="zipCode" label="Zip code" />
                <div data-testid="card-number" style={{ position: "relative" }}>
                  <CardNumberElement
                    options={elementOptions}
                    className={styles.inputText}
                  />
                  <div className={styles.cardIcons}>
                    <i className="fab fa-cc-visa"></i>
                    <i className="fab fa-cc-amex"></i>
                    <i className="fab fa-cc-mastercard"></i>
                    <i className="fab fa-cc-discover"></i>
                  </div>
                </div>
                <div className={styles.twoColumn}>
                  <CardExpiryElement
                    options={elementOptions}
                    className={styles.inputText}
                    data-testid="card-expiry"
                  />
                  <CardCvcElement
                    options={elementOptions}
                    className={styles.inputText}
                    data-testid="card-cvc"
                  />
                </div>
              </div>
              <div className="footer-buttons">
                <Button type="button" onClick={onClose} variant="secondary">
                  Cancel
                </Button>
                <Button
                  data-testid="button-submit"
                  type="submit"
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CustomModal>
  );
};

export default function AddCardWithElements(props: Props) {
  return (
    <Elements stripe={stripePromise}>
      <AddCardModal {...props} />
    </Elements>
  );
}
